/**
 * Space item detail helpers
 *
 * Canonical item-detail loaders for Space surfaces.
 *
 * Primary responsibilities:
 * - Load a specific Space item by type and id
 * - Ensure the loaded item still belongs to the active Space
 *
 * Dependencies:
 * - Space item-detail API route
 * - Space selection types
 */

import type {
  SelectedSpaceItem,
  SpaceConversation,
  SpaceFolder,
  SpaceNote,
  SpaceSelectableItemType,
  SpaceSheet,
  SpaceSprint,
  SpaceTask
} from "@/types/spaces"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import {
  fetchSpaceConversation,
  SpaceConversationNotFoundError
} from "@/lib/spaces/space-conversation-client"

export type SpaceItemDetailTarget<T extends SpaceSelectableItemType = SpaceSelectableItemType> = {
  type: T
  id: string
}

type SpaceItemDetailTypeMap = {
  task: SpaceTask
  sheet: SpaceSheet
  note: SpaceNote
  sprint: SpaceSprint
  folder: SpaceFolder
  conversation: SpaceConversation
}

export type SpaceItemDetailData<
  T extends SpaceSelectableItemType = SpaceSelectableItemType
> = SpaceItemDetailTypeMap[T]

const isItemInSpace = (
  data: { space_id?: string | null } | null,
  spaceId: string
) => {
  if (!data) {
    return false
  }

  if (!data.space_id) {
    return true
  }

  return data.space_id === spaceId
}

const readSpaceItemDetailError = async (
  response: Response,
  fallback: string
) => {
  const responseText = await response.text()

  if (!responseText) {
    return fallback
  }

  try {
    const payload = JSON.parse(responseText) as unknown

    if (
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
    ) {
      return payload.error
    }
  } catch {
    return responseText
  }

  return responseText
}

export const fetchSpaceItemDetail = async <
  T extends SpaceSelectableItemType
>(
  spaceId: string,
  item: SpaceItemDetailTarget<T>
): Promise<SpaceItemDetailData<T> | null> => {
  if (item.type === "conversation") {
    try {
      const conversation = await fetchSpaceConversation(spaceId, item.id)
      return isItemInSpace(conversation, spaceId)
        ? (conversation as SpaceItemDetailData<T>)
        : null
    } catch (error) {
      if (error instanceof SpaceConversationNotFoundError) {
        return null
      }

      throw error
    }
  }

  const searchParams = new URLSearchParams()
  searchParams.set("type", item.type)
  searchParams.set("itemId", item.id)

  const response = await authenticatedFetch(
    `/api/spaces/${spaceId}/item-detail?${searchParams.toString()}`,
    {
      cache: "no-store"
    }
  )

  if (!response.ok) {
    throw new Error(
      await readSpaceItemDetailError(response, `Failed to load ${item.type}`)
    )
  }

  const data = (await response.json()) as SpaceItemDetailData<T> | null

  return isItemInSpace(data, spaceId)
    ? data
    : null
}

export const toSelectedSpaceItem = <
  T extends SpaceSelectableItemType
>(
  item: SpaceItemDetailTarget<T>,
  data: SpaceItemDetailData<T>
): SelectedSpaceItem => {
  return {
    type: item.type,
    id: item.id,
    data
  } as SelectedSpaceItem
}
